import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ROLES_ALLOWED_TO_BY_PASS_REQUESTED_TO,
  STATUS_ALLOWED_FOR_VALIDATION_ACTION,
} from '@app/features/contracts/validation/validation-request-service';
import { ValidationRequestType } from '@app/features/contracts/validation/validation-request.model';
import { UserRole } from '@app/features/users/user.model';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import {
  CommentDialog,
  CommentDialogData,
} from '@app/shared/dialogs/comment-dialog/comment-dialog';
import { ValidationCircuits } from '@app/features/contracts/validation/validation-circuit';
import { ValidationSubjectItem } from '@app/features/contracts/validation/validation-subject.model';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { filter, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-button-refuse-validation-request',
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './button-refuse-validation-request.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './button-refuse-validation-request.scss',
})
export class ButtonRefuseValidationRequest extends RxjsDirective {
  /** Le sujet validé : un contrat d'embauche, ou un avenant. */
  contract = input.required<ValidationSubjectItem>();
  private readonly validationCircuits = inject(ValidationCircuits);
  protected circuit = () => this.validationCircuits.of(this.contract());
  validationType = input.required<ValidationRequestType>();
  validationRequestRefused = output<void>();

  dialog = inject(MatDialog);
  authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);

  rolesAllowedToByPassRequestedTo: UserRole[] = [];

  constructor() {
    super();
    effect(() => {
      this.rolesAllowedToByPassRequestedTo =
        ROLES_ALLOWED_TO_BY_PASS_REQUESTED_TO[this.validationType()] ?? [];
    });
  }

  get canRefuseValidation(): boolean {
    const validationRequests = this.contract().validationRequests ?? [];
    const pendingValidationRequests = validationRequests.filter(
      (req) => req.type === this.validationType() && req.status === 'PENDING',
    );
    const hasPendingValidationRequest = pendingValidationRequests.length > 0;
    const isRequestedToCurrentUser = pendingValidationRequests.some(
      (req) => req.requestedTo === this.authenticatedUserService.userId,
    );
    const isBypassRole = this.rolesAllowedToByPassRequestedTo.some((role) =>
      this.authenticatedUserService.realmRoles.includes(role),
    );

    return (
      STATUS_ALLOWED_FOR_VALIDATION_ACTION[this.validationType()].includes(
        this.contract().status,
      ) &&
      hasPendingValidationRequest &&
      (isRequestedToCurrentUser || isBypassRole)
    );
  }

  refuseValidation() {
    const dialogRef = this.dialog.open<CommentDialog, CommentDialogData, string | undefined>(
      CommentDialog,
      {
        data: {
          title: 'Motif de demande de correction',
          required: true,
        },
        minWidth: '700px',
      },
    );

    this.register(
      dialogRef
        .afterClosed()
        .pipe(
          filter((comment: string | undefined): comment is string => comment !== undefined),
          switchMap((comment: string) => this.circuit().refuse(this.contract().id, comment || '')),
          tap(() => this.validationRequestRefused.emit()),
        )
        .subscribe(),
    );
  }
}
